// PRE RMA CSB — Saisie vocale du registre de consultation (RCE) : panneau intégré à l'écran « Saisie RCE ».
//
// Ce composant ne contient AUCUNE règle métier : toute la logique (colonnes, interprétation, validation, doublons, lot à enregistrer)
// est dans shared/business-logic/saisieVocale.ts (testée). Ici : micro, affichage, boutons.
// Enregistrement : UNIQUEMENT via importerConsultations (file d'attente IndexedDB existante, en une transaction « tout ou rien », puis
// synchronisation normale) — aucun appel réseau propre, aucun formulaire parallèle, aucune modification des consultations existantes.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  appliquer, creerSaisie, analyserLigne, recapitulatif, preparerLot, serialiserBrouillon, lireBrouillon, colonneDe, affichageCellule,
  ligneVide, curseur, estResolue, validerOrdre, ORDRE_PAR_DEFAUT,
} from '../../../shared/business-logic/saisieVocale.ts';
import type { Action, Cellule, ContexteSaisie, EtatSaisie, IdColonne } from '../../../shared/business-logic/saisieVocale.ts';
import { libelleRegistre } from '../../../shared/business-logic/registre.ts';
import type { Registre } from '../../../shared/business-logic/registre.ts';
import { importerConsultations } from '../offline/syncQueue.ts';
import { Pastille } from './ui.tsx';
import { creerMoteur, dicteeDisponible, contexteSecurise, messageErreurVocale, lireAVoixHaute, preparerMicro } from '../voice/moteurVocal.ts';
import type { MoteurVocal } from '../voice/moteurVocal.ts';

const CLE_CONSENTEMENT = 'prerma_voix_consentement';
const CLE_LANGUE = 'prerma_voix_langue';
const CLE_ORDRE = 'prerma_saisie_vocale_ordre';
const CLE_CONTINU = 'prerma_voix_continu';
const estAndroid = () => typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
const LANGUES = [
  { code: 'fr-FR', libelle: 'Français (pris en charge)' },
  { code: 'mg-MG', libelle: 'Malagasy (expérimental, non vérifié)' },
];

interface Props {
  periode: { id: string; annee: number; mois: number };
  csbId: string;
  registre: Registre;
  /** Consultations déjà présentes pour la période (serveur + file locale) : sert à refuser les doublons. */
  existants: Array<{ numeroRceBrut: string; ageBrut?: string | null }>;
  dateParDefaut: string;
  aujourdhui: string;
  onEnregistre: (nombre: number) => void;
}

const lire = (cle: string): string | null => { try { return localStorage.getItem(cle); } catch { return null; } };
const ecrire = (cle: string, v: string | null) => { try { if (v === null) localStorage.removeItem(cle); else localStorage.setItem(cle, v); } catch { /* stockage indisponible : le travail reste à l'écran */ } };

function ordreSauvegarde(): IdColonne[] {
  try { const v = validerOrdre(JSON.parse(lire(CLE_ORDRE) ?? 'null')); if (v.ok) return v.ordre; } catch { /* ordre illisible : ordre par défaut */ }
  return [...ORDRE_PAR_DEFAUT];
}

function pastilleCellule(c: Cellule, courant: boolean) {
  if (c.statut === 'RECONNU') return <Pastille niveau="ok">Valeur reconnue</Pastille>;
  if (c.statut === 'ABSENT') return <Pastille niveau="info">Vide</Pastille>;
  if (c.statut === 'A_VERIFIER') return <Pastille niveau="warn">À vérifier</Pastille>;
  if (c.statut === 'ERREUR') return <Pastille niveau="bad">Erreur à corriger</Pastille>;
  return <Pastille niveau="info">{courant ? 'À dicter maintenant' : 'Attendu'}</Pastille>;
}

/** Tableau de contrôle d'UNE ligne : colonne, valeur entendue, champ + valeur dans le formulaire, état, modification individuelle. */
function TableauLigne({ etat, index, ctx, envoyer }: { etat: EtatSaisie; index: number; ctx: ContexteSaisie; envoyer: (a: Action) => void }) {
  const ligne = etat.lignes[index];
  const analyse = useMemo(() => analyserLigne(etat, index, ctx), [etat, index, ctx]);
  const [saisies, setSaisies] = useState<Record<number, string>>({});
  const cur = curseur(ligne);
  return (
    <div className="defil vocal-tableau-bloc">
      <table className="vocal-tableau">
        <thead><tr><th>#</th><th>Colonne du registre</th><th>Valeur entendue</th><th>Champ PRE RMA = valeur</th><th>État</th><th>Modifier</th></tr></thead>
        <tbody>
          {ligne.cellules.map((c, k) => {
            const col = colonneDe(c.colonne);
            const problemes = analyse.bloquants.filter((b) => b.colonne === c.colonne);
            const courant = index === etat.courante && (etat.cible ?? cur) === k;
            return (
              <tr key={c.colonne} className={(courant ? 'vocal-courant ' : '') + (c.statut === 'A_VERIFIER' || c.statut === 'ERREUR' || problemes.length ? 'vocal-erreur' : '')}>
                <td data-l="#">{k + 1}</td>
                <td data-l="Colonne"><b>{col.libelle}</b></td>
                <td data-l="Entendu">{c.brut || '—'}</td>
                <td data-l="Formulaire"><code>{col.champ}</code> = <b>{affichageCellule(c)}</b></td>
                <td data-l="État">
                  {pastilleCellule(c, courant)}
                  {c.message && c.statut !== 'RECONNU' && <small className="note vocal-msg">{c.message}</small>}
                  {problemes.map((p, i) => <small key={i} className="vocal-msg vocal-bad">{p.message}</small>)}
                </td>
                <td data-l="Modifier" className="vocal-modifier">
                  <input aria-label={`Corriger : ${col.libelle}`} value={saisies[k] ?? ''} placeholder="Nouvelle valeur"
                    onChange={(e) => setSaisies({ ...saisies, [k]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (saisies[k] ?? '').trim()) { envoyer({ type: 'EDITER', ligne: index, colonne: k, texte: saisies[k] }); setSaisies({ ...saisies, [k]: '' }); } }} />
                  <div className="vocal-mini-boutons">
                    <button type="button" className="secondaire" disabled={!(saisies[k] ?? '').trim()} onClick={() => { envoyer({ type: 'EDITER', ligne: index, colonne: k, texte: saisies[k] }); setSaisies({ ...saisies, [k]: '' }); }}>OK</button>
                    <button type="button" className="secondaire" onClick={() => envoyer({ type: 'VIDER_CELLULE', ligne: index, colonne: k })}>Vide</button>
                    {c.proposition && <button type="button" onClick={() => envoyer({ type: 'ACCEPTER_PROPOSITION', ligne: index, colonne: k })}>Accepter « {c.proposition} »</button>}
                    {(c.candidats ?? []).map((cd) => (
                      <button type="button" className="secondaire" key={cd} onClick={() => envoyer({ type: 'EDITER', ligne: index, colonne: k, texte: cd })}>{affichageCellule({ ...c, valeur: cd, statut: 'RECONNU' })}</button>
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function pastilleLigne(etatLigne: string, validee: boolean) {
  if (validee && etatLigne === 'PRETE') return <Pastille niveau="ok">Ligne validée</Pastille>;
  if (etatLigne === 'PRETE') return <Pastille niveau="ok">Ligne prête à valider</Pastille>;
  if (etatLigne === 'A_CORRIGER') return <Pastille niveau="bad">Erreur nécessitant une correction</Pastille>;
  return <Pastille niveau="warn">Ligne incomplète</Pastille>;
}

export default function SaisieVocale({ periode, csbId, registre, existants, dateParDefaut, aujourdhui, onEnregistre }: Props) {
  const cleBrouillon = `prerma_saisie_vocale_${csbId}_${periode.id}`;
  const [repris, setRepris] = useState(false);
  const [etat, setEtat] = useState<EtatSaisie>(() => {
    const b = lireBrouillon(lire(cleBrouillon));
    if (b && b.lignes.some((l) => !ligneVide(l))) return b;
    return creerSaisie({ ordre: ordreSauvegarde(), dateConsultation: dateParDefaut });
  });
  const [langue, setLangue] = useState<string>(() => lire(CLE_LANGUE) ?? 'fr-FR');
  // Écoute continue (ordinateur) ou « une valeur à la fois » (Android : le mode continu y est souvent peu fiable).
  const [continu, setContinu] = useState<boolean>(() => { const v = lire(CLE_CONTINU); return v === null ? !estAndroid() : v === '1'; });
  const [consenti, setConsenti] = useState<boolean>(() => lire(CLE_CONSENTEMENT) === '1');
  const [microVoulu, setMicroVoulu] = useState(false);
  const [ecoute, setEcoute] = useState(false);
  const [entendu, setEntendu] = useState('');
  const [erreurMicro, setErreurMicro] = useState<string | null>(null);
  const [enLigne, setEnLigne] = useState<boolean>(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [texteClavier, setTexteClavier] = useState('');
  // Micro du clavier Google (Gboard) : même moteur vocal que la recherche Google ; activé tout seul si le micro intégré échoue.
  const [clavierGoogle, setClavierGoogle] = useState(false);
  const champClavier = useRef<HTMLInputElement | null>(null);
  const focaliserClavier = () => { setTimeout(() => champClavier.current?.focus(), 80); };
  const [erreursEnreg, setErreursEnreg] = useState<string[]>([]);
  const [succes, setSucces] = useState<string | null>(null);
  const [enregEnCours, setEnregEnCours] = useState(false);

  const ctx: ContexteSaisie = useMemo(() => ({ registre, periode: { annee: periode.annee, mois: periode.mois }, aujourdhui, existants }), [registre, periode.annee, periode.mois, aujourdhui, existants]);
  const ctxRef = useRef(ctx); ctxRef.current = ctx;
  const envoyer = useCallback((a: Action) => setEtat((prev) => appliquer(prev, a, ctxRef.current)), []);
  const moteurRef = useRef<MoteurVocal | null>(null);
  const lectureRef = useRef(false); // vrai pendant la lecture à voix haute : l'application ne doit pas « s'entendre »

  // ---- Brouillon : conservé à chaque changement (jamais d'audio) ; retiré quand il n'y a plus rien à garder.
  useEffect(() => {
    const contenu = etat.lignes.some((l) => !ligneVide(l));
    ecrire(cleBrouillon, contenu ? serialiserBrouillon(etat) : null);
  }, [etat, cleBrouillon]);
  useEffect(() => { if (etat.lignes.some((l) => !ligneVide(l)) && lire(cleBrouillon)) setRepris(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // ---- Connexion
  useEffect(() => {
    const maj = () => setEnLigne(navigator.onLine);
    window.addEventListener('online', maj); window.addEventListener('offline', maj);
    return () => { window.removeEventListener('online', maj); window.removeEventListener('offline', maj); };
  }, []);

  // ---- Moteur vocal (recréé si la langue change). Seuls les résultats FINAUX sont transmis ; un silence n'envoie rien.
  useEffect(() => {
    const m = creerMoteur({
      langue,
      continu,
      onFinal: (texte, confiance) => { if (!lectureRef.current) envoyer({ type: 'TEXTE', texte, confiance, source: 'VOIX' }); },
      onInterim: setEntendu,
      onEcoute: setEcoute,
      onErreur: (code, detail) => {
        setMicroVoulu(false);
        setErreurMicro(`${messageErreurVocale(code)}${detail && code === 'inconnue' ? ` (${detail})` : ''} Utilisez le micro du clavier Google dans le champ ci-dessous.`);
        setClavierGoogle(true); focaliserClavier();
      },
    });
    moteurRef.current = m;
    return () => { m.arreter(); moteurRef.current = null; };
  }, [langue, continu, envoyer]);

  // Le micro tourne seulement en écoute ou en pause (pour entendre « reprendre ») ; jamais en récapitulatif ni inactif.
  useEffect(() => {
    const m = moteurRef.current;
    if (!m) return;
    if (microVoulu && (etat.mode === 'ECOUTE' || etat.mode === 'PAUSE')) m.demarrer();
    else { m.arreter(); if (microVoulu && (etat.mode === 'RECAP' || etat.mode === 'INACTIF')) setMicroVoulu(false); }
  }, [microVoulu, etat.mode, langue, continu]);

  // Dès que le micro écoute vraiment, une ancienne alerte de démarrage n'a plus lieu d'être.
  useEffect(() => { if (ecoute) setErreurMicro(null); }, [ecoute]);

  // Surveillance : si le micro ne démarre pas en 6 s, on le dit clairement (au lieu de rester muet).
  useEffect(() => {
    if (!microVoulu || ecoute || etat.mode === 'RECAP' || etat.mode === 'INACTIF') return;
    const t = setTimeout(() => {
      setErreurMicro('Le micro ne démarre pas. Vérifiez : 1) vous êtes dans Chrome (pas dans un navigateur intégré à une autre application) ; 2) l’autorisation « Micro » du site (cadenas dans la barre d’adresse, ou Paramètres de Chrome > Paramètres des sites > Micro) ; 3) la connexion Internet. En attendant, utilisez le micro du clavier Google dans le champ ci-dessous.');
      setClavierGoogle(true); focaliserClavier();
    }, 6000);
    return () => clearTimeout(t);
  }, [microVoulu, ecoute, etat.mode]);

  // « Répéter » : lecture locale de la dernière valeur.
  useEffect(() => {
    if (!etat.annonce) return;
    lectureRef.current = true;
    lireAVoixHaute(etat.annonce.texte, langue, () => { setTimeout(() => { lectureRef.current = false; }, 700); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etat.annonce?.n]);
  useEffect(() => () => { try { window.speechSynthesis?.cancel(); } catch { /* rien */ } }, []);

  /** Bascule vers le micro du clavier Google (Gboard) : démarre la session si besoin, puis place le curseur dans le champ de saisie. */
  function basculerClavierGoogle() {
    if (etat.mode === 'INACTIF') envoyer({ type: 'COMMENCER' });
    setClavierGoogle(true); focaliserClavier();
  }
  async function demarrerMicro() {
    if (!consenti) return;
    if (!dicteeDisponible()) { setErreurMicro(`${messageErreurVocale('non-supporte')} Utilisez le micro du clavier Google dans le champ ci-dessous.`); basculerClavierGoogle(); return; }
    setErreurMicro(null);
    // Comme Google : autorisation du micro demandée dans le geste de l'utilisateur, puis reconnaissance. La vraie cause d'un échec est affichée.
    const p = await preparerMicro();
    if (!p.ok) { setMicroVoulu(false); setErreurMicro(`${messageErreurVocale(p.code)} Utilisez le micro du clavier Google dans le champ ci-dessous.`); basculerClavierGoogle(); return; }
    moteurRef.current?.demarrer();
    setMicroVoulu(true);
  }
  function accepterConfidentialite() { ecrire(CLE_CONSENTEMENT, '1'); setConsenti(true); }
  function commencer() {
    envoyer({ type: 'COMMENCER' });
    if (!consenti) { setErreurMicro('Le micro est désactivé : touchez d’abord le bouton « J’ai compris : autoriser la dictée » dans l’encadré jaune en haut. La saisie au clavier fonctionne déjà.'); return; }
    void demarrerMicro();
  }
  function reprendre() {
    envoyer({ type: 'REPRENDRE' });
    if (!consenti) { setErreurMicro('Le micro est désactivé : touchez d’abord « J’ai compris : autoriser la dictée » (encadré jaune en haut).'); return; }
    void demarrerMicro();
  }
  function couperMicro() { moteurRef.current?.arreter(); setMicroVoulu(false); if (etat.mode === 'ECOUTE') envoyer({ type: 'PAUSE' }); }
  function envoyerClavier() {
    const t = texteClavier.trim();
    if (!t) return;
    envoyer({ type: 'TEXTE', texte: t, source: 'CLAVIER' });
    setTexteClavier('');
  }

  const ligne = etat.lignes[etat.courante];
  const toutesVides = etat.lignes.every(ligneVide);
  const recues = ligne.cellules.filter(estResolue).length;
  const cur = curseur(ligne);
  const colCourante = ligne.cellules[etat.cible ?? (cur >= 0 ? cur : 0)];
  const analyseCourante = useMemo(() => analyserLigne(etat, etat.courante, ctx), [etat, ctx]);
  const recap = useMemo(() => recapitulatif(etat, ctx), [etat, ctx]);
  const nbValidees = etat.lignes.filter((l) => l.validee).length;

  let statut: { classe: string; texte: string };
  if (etat.mode === 'RECAP') statut = { classe: 'recap', texte: 'Récapitulatif : vérification finale' };
  else if (!microVoulu && etat.mode !== 'ECOUTE') statut = { classe: 'inactif', texte: etat.mode === 'PAUSE' ? 'En pause · microphone inactif' : 'Microphone inactif' };
  else if (etat.mode === 'PAUSE') statut = { classe: 'pause', texte: 'En pause · dites « reprendre »' };
  else if (!microVoulu) statut = { classe: 'inactif', texte: 'Microphone inactif · saisie au clavier' };
  else if (entendu) statut = { classe: 'reco', texte: 'Reconnaissance en cours…' };
  else if (ecoute) statut = { classe: 'ecoute', texte: '● Écoute en cours' };
  else statut = { classe: 'ecoute', texte: 'Démarrage du microphone…' };

  // ---- Configuration (modifiable seulement tant qu'aucune ligne n'est dictée)
  function changerOrdre(i: number, delta: number) {
    const o = [...etat.ordre]; const j = i + delta;
    if (j < 0 || j >= o.length) return;
    [o[i], o[j]] = [o[j], o[i]];
    ecrire(CLE_ORDRE, JSON.stringify(o));
    setEtat(creerSaisie({ ordre: o, premierePosition: etat.lignes[0].position, dateConsultation: etat.dateConsultation }));
  }
  function changerPremierePosition(n: number) {
    if (!Number.isFinite(n) || n < 1) return;
    setEtat(creerSaisie({ ordre: etat.ordre, premierePosition: Math.floor(n), dateConsultation: etat.dateConsultation }));
  }
  function repartir() {
    const derniere = etat.lignes[etat.lignes.length - 1].position;
    ecrire(cleBrouillon, null);
    setEtat(creerSaisie({ ordre: etat.ordre, premierePosition: derniere + 1, dateConsultation: etat.dateConsultation }));
  }
  function abandonnerBrouillon() {
    ecrire(cleBrouillon, null); setRepris(false);
    setEtat(creerSaisie({ ordre: etat.ordre, dateConsultation: dateParDefaut }));
  }

  async function confirmerEtEnregistrer() {
    setErreursEnreg([]); setSucces(null);
    const p = preparerLot(etat, ctxRef.current); // re-contrôle complet juste avant l'écriture
    if (!p.ok) { setErreursEnreg(p.raisons); return; }
    setEnregEnCours(true);
    try {
      const n = await importerConsultations(periode.id, csbId, p.lot);
      setSucces(`${n} consultation(s) enregistrée(s) sur cet appareil. Elles partent vers le serveur dès que la connexion le permet (comme une saisie normale).`);
      repartir(); setRepris(false);
      onEnregistre(n);
    } catch (err: unknown) {
      setErreursEnreg([`${err instanceof Error ? err.message : 'Écriture locale impossible'} — rien n’a été enregistré ; votre dictée est conservée.`]);
    } finally { setEnregEnCours(false); }
  }

  const minDate = `${periode.annee}-${String(periode.mois).padStart(2, '0')}-01`;
  const maxDate = `${periode.annee}-${String(periode.mois).padStart(2, '0')}-${String(new Date(periode.annee, periode.mois, 0).getDate()).padStart(2, '0')}`;

  return (
    <section className="carte vocal" aria-label="Saisie vocale du registre">
      <h2>🎙️ Saisie vocale — {libelleRegistre(registre)}</h2>

      {repris && etat.mode !== 'RECAP' && (
        <div className="message warn" role="status">
          Votre dictée précédente a été reprise ({recap.lignesDictees} ligne(s)). Le micro n’est pas relancé tout seul.{' '}
          <button type="button" className="secondaire" onClick={abandonnerBrouillon}>Abandonner ce brouillon</button>
        </div>
      )}

      {!consenti && (
        <div className="message warn" role="note">
          <b>Avant d’activer le micro — confidentialité.</b> La dictée utilise le service de reconnaissance vocale de votre navigateur : avec Chrome, l’audio est envoyé à Google.
          PRE RMA n’enregistre aucun fichier audio. Ne dictez ni nom, ni adresse, ni numéro de téléphone : seulement les valeurs du registre. Internet est nécessaire.
          <div style={{ marginTop: 8 }}><button type="button" onClick={accepterConfidentialite}>J’ai compris : autoriser la dictée</button></div>
          <small className="note">Sans cet accord, vous pouvez quand même saisir ligne par ligne au clavier (champ ci-dessous).</small>
        </div>
      )}
      {!dicteeDisponible() && <div className="message bad" role="alert">Ce navigateur ne propose pas la dictée vocale. Utilisez Chrome (Android ou ordinateur) ou la saisie au clavier ci-dessous — le clavier Android a aussi son propre micro.</div>}
      {dicteeDisponible() && !contexteSecurise() && <div className="message bad" role="alert">Le micro exige une connexion sécurisée (https). Dictée indisponible sur cette adresse.</div>}
      {!enLigne && <div className="message warn" role="status">Pas de connexion Internet : la reconnaissance vocale risque de ne pas fonctionner (non vérifié hors connexion). La saisie au clavier, le contrôle et l’enregistrement sur l’appareil restent possibles ; vos lignes sont conservées.</div>}
      {erreurMicro && <div className="message bad" role="alert">{erreurMicro}</div>}

      <div className={`vocal-statut ${statut.classe}`} role="status" aria-live="polite">{statut.texte}</div>
      {entendu && <p className="vocal-entendu">J’entends : « {entendu} »</p>}

      {etat.mode !== 'RECAP' && (
        <>
          <details className="vocal-config">
            <summary>Réglages (langue, date, ordre des colonnes)</summary>
            <label htmlFor="vocal-langue">Langue de reconnaissance</label>
            <select id="vocal-langue" value={langue} onChange={(e) => { setLangue(e.target.value); ecrire(CLE_LANGUE, e.target.value); }}>
              {LANGUES.map((l) => <option key={l.code} value={l.code}>{l.libelle}</option>)}
            </select>
            <label htmlFor="vocal-mode">Mode d’écoute</label>
            <select id="vocal-mode" value={continu ? '1' : '0'} onChange={(e) => { setContinu(e.target.value === '1'); ecrire(CLE_CONTINU, e.target.value); }}>
              <option value="0">Une valeur à la fois (conseillé sur Android)</option>
              <option value="1">Écoute continue (conseillé sur ordinateur)</option>
            </select>
            <label htmlFor="vocal-date">Date de consultation de la ligne en cours (et des suivantes)</label>
            <input id="vocal-date" type="date" value={ligne.date} min={minDate} max={maxDate} onChange={(e) => envoyer({ type: 'DATE_COURANTE', date: e.target.value })} />
            <small className="note">Vocalement : « date douze octobre ».</small>
            <label htmlFor="vocal-pos">Numéro de la première ligne du registre à dicter</label>
            <input id="vocal-pos" type="number" min={1} value={etat.lignes[0].position} disabled={!toutesVides || etat.mode !== 'INACTIF'} onChange={(e) => changerPremierePosition(Number(e.target.value))} />
            <p className="note"><b>Ordre des colonnes</b> — doit être identique à celui de votre registre papier, de gauche à droite.
              {!toutesVides && ' (Modifiable seulement avant la première ligne.)'}</p>
            <ol className="vocal-ordre">
              {etat.ordre.map((id, i) => (
                <li key={id}>
                  <span>{colonneDe(id).libelle}</span>
                  <button type="button" className="secondaire" aria-label={`Monter ${colonneDe(id).libelle}`} disabled={!toutesVides || etat.mode !== 'INACTIF' || i === 0} onClick={() => changerOrdre(i, -1)}>↑</button>
                  <button type="button" className="secondaire" aria-label={`Descendre ${colonneDe(id).libelle}`} disabled={!toutesVides || etat.mode !== 'INACTIF' || i === etat.ordre.length - 1} onClick={() => changerOrdre(i, 1)}>↓</button>
                </li>
              ))}
            </ol>
          </details>

          <div className="vocal-progression">
            <div><b>Ligne {ligne.position} du registre</b> · {recues} valeur(s) reçue(s) sur {etat.ordre.length} attendues</div>
            <progress max={etat.ordre.length} value={recues} aria-label="Valeurs reçues" />
            {cur >= 0 || etat.cible !== null ? (
              <div className="vocal-attendu">
                {etat.cible !== null ? 'Redites' : 'Dites'} la colonne {etat.ordre.indexOf(colCourante.colonne) + 1}/{etat.ordre.length} : <b>{colonneDe(colCourante.colonne).libelle}</b>
                <small className="note" style={{ display: 'block' }}>Ex. {colonneDe(colCourante.colonne).exemple}. Case vide : dites « vide ».</small>
              </div>
            ) : <div className="vocal-attendu">Ligne complète : vérifiez le tableau puis dites « validé » ou appuyez sur « Valider cette ligne ».</div>}
          </div>

          {etat.message && <div className={`message ${etat.message.niveau === 'bad' ? 'bad' : etat.message.niveau === 'warn' ? 'warn' : 'ok'}`} role="status" aria-live="polite">{etat.message.texte}</div>}

          <div className="vocal-boutons">
            {etat.mode === 'INACTIF' && <button type="button" className="vocal-gros" onClick={commencer}>🎙️ Commencer</button>}
            {etat.mode === 'ECOUTE' && <button type="button" className="vocal-gros secondaire" onClick={() => envoyer({ type: 'PAUSE' })}>⏸ Pause</button>}
            {etat.mode === 'PAUSE' && <button type="button" className="vocal-gros" onClick={reprendre}>▶ Reprendre</button>}
            {microVoulu && <button type="button" className="vocal-gros secondaire" onClick={couperMicro}>🔇 Couper le micro</button>}
            <button type="button" className="vocal-gros secondaire" disabled={etat.mode === 'INACTIF' || recues === 0} onClick={() => envoyer({ type: 'REPETER' })}>🔁 Répéter la dernière valeur</button>
            <button type="button" className="vocal-gros secondaire" disabled={etat.mode === 'INACTIF' || (recues === 0 && cur !== 0)} onClick={() => envoyer({ type: 'CORRIGER' })}>✏️ Corriger la dernière valeur</button>
            <button type="button" className="vocal-gros secondaire" disabled={etat.mode === 'INACTIF' || ligneVide(ligne)} onClick={() => envoyer({ type: 'ANNULER_LIGNE' })}>🗑 Annuler la ligne</button>
            <button type="button" className="vocal-gros secondaire" disabled={ligneVide(ligne)} onClick={() => envoyer({ type: 'VERIFIER' })}>🔎 Vérifier la ligne</button>
            <button type="button" className="vocal-gros vocal-valider" disabled={analyseCourante.etat !== 'PRETE'} onClick={() => envoyer({ type: 'VALIDER_LIGNE', ligne: etat.courante })}>✅ Valider cette ligne</button>
            <button type="button" className="vocal-gros secondaire" disabled={toutesVides} onClick={() => envoyer({ type: 'TERMINER' })}>🏁 Terminer la saisie</button>
          </div>

          <div className="vocal-clavier">
            <button type="button" className="vocal-gros secondaire" onClick={basculerClavierGoogle}>⌨️🎤 Dictée par le clavier Google</button>
            {clavierGoogle && <div className="message ok" role="note">Touchez le champ ci-dessous, puis le micro 🎤 du clavier (Gboard) : c’est le même moteur vocal que la recherche Google. Dites la valeur, puis touchez Entrée / Envoyer.</div>}
            <label htmlFor="vocal-clavier">Valeur attendue (saisie ou dictée avec le micro du clavier)</label>
            <div className="ligne-actions">
              <input id="vocal-clavier" ref={champClavier} lang={langue} enterKeyHint="send" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={texteClavier} onChange={(e) => setTexteClavier(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') envoyerClavier(); }}
                placeholder={etat.mode === 'INACTIF' ? 'Appuyez d’abord sur « Commencer »' : 'ex. douze · 10 ans · vide · validé'} disabled={etat.mode === 'INACTIF'} autoComplete="off" />
              <button type="button" onClick={envoyerClavier} disabled={etat.mode === 'INACTIF' || !texteClavier.trim()}>Envoyer</button>
            </div>
          </div>

          <h3 className="vocal-titre-tableau">Contrôle de la ligne {ligne.position} {pastilleLigne(analyseCourante.etat, ligne.validee)}</h3>
          {analyseCourante.bloquants.length > 0 && (
            <ul className="messages-erreur">{analyseCourante.bloquants.map((b, i) => <li key={i}>{b.message}</li>)}</ul>
          )}
          <TableauLigne etat={etat} index={etat.courante} ctx={ctx} envoyer={envoyer} />
          <p className="note">{nbValidees} ligne(s) validée(s) pour l’instant{nbValidees > 0 && ' — elles ne sont pas encore enregistrées : terminez la saisie pour les vérifier et les enregistrer.'}</p>

          <details className="vocal-aide">
            <summary>Commandes vocales</summary>
            <p className="note">Dites chaque valeur puis faites une courte pause. Commandes (phrase seule) : « commencer » · « pause » · « reprendre » · « répéter » · « corriger » · « annuler la ligne » · « vérifier » · « validé » · « terminer » · « fin de ligne » (toutes les colonnes restantes sont vides). Plusieurs N° RCE : « douze virgule treize ».</p>
          </details>
        </>
      )}

      {etat.mode === 'RECAP' && (
        <div className="vocal-recap">
          <h3>Vérification finale avant enregistrement</h3>
          <dl className="vocal-compteurs">
            <div><dt>Lignes dictées</dt><dd>{recap.lignesDictees}</dd></div>
            <div><dt>Lignes vérifiées</dt><dd>{recap.lignesVerifiees}</dd></div>
            <div><dt>Lignes validées</dt><dd>{recap.lignesValidees}</dd></div>
            <div className={recap.valeursACorriger ? 'vocal-bad' : ''}><dt>Valeurs à corriger</dt><dd>{recap.valeursACorriger}</dd></div>
            <div className={recap.champsObligatoiresManquants ? 'vocal-bad' : ''}><dt>Champs obligatoires manquants</dt><dd>{recap.champsObligatoiresManquants}</dd></div>
            <div className={recap.erreursCoherence ? 'vocal-bad' : ''}><dt>Erreurs de cohérence</dt><dd>{recap.erreursCoherence}</dd></div>
          </dl>
          {etat.message && <div className={`message ${etat.message.niveau === 'bad' ? 'bad' : 'ok'}`} role="status">{etat.message.texte}</div>}

          {recap.parLigne.map((p) => {
            const l = etat.lignes[p.index];
            const rce = l.cellules.find((c) => c.colonne === 'rce');
            return (
              <details key={p.index} className="vocal-recap-ligne" open={!p.validee || p.etat !== 'VALIDEE'}>
                <summary>
                  Ligne {p.position} du registre · N° RCE {rce && rce.statut !== 'ATTENDU' ? affichageCellule(rce) : '—'} {pastilleLigne(p.etat === 'VALIDEE' ? 'PRETE' : p.etat, p.validee)}
                </summary>
                <label htmlFor={`vocal-date-${p.index}`}>Date de consultation</label>
                <input id={`vocal-date-${p.index}`} type="date" value={l.date} min={minDate} max={maxDate} onChange={(e) => envoyer({ type: 'DATE_LIGNE', ligne: p.index, date: e.target.value })} />
                {p.bloquants.length > 0 && <ul className="messages-erreur">{p.bloquants.map((b, i) => <li key={i}>{b.message}</li>)}</ul>}
                <TableauLigne etat={etat} index={p.index} ctx={ctx} envoyer={envoyer} />
                {!p.validee && <button type="button" className="vocal-gros vocal-valider" disabled={p.etat !== 'PRETE'} onClick={() => envoyer({ type: 'VALIDER_LIGNE', ligne: p.index })}>✅ Valider cette ligne</button>}
              </details>
            );
          })}

          <div className="vocal-boutons">
            <button type="button" className="vocal-gros secondaire" onClick={reprendre}>↩ Continuer la dictée</button>
          </div>
          {erreursEnreg.length > 0 && <ul className="messages-erreur" role="alert">{erreursEnreg.map((r, i) => <li key={i}>{r}</li>)}</ul>}
          <div className="vocal-confirmation">
            <button type="button" className="vocal-confirmer" disabled={!recap.pretAEnregistrer || enregEnCours} onClick={confirmerEtEnregistrer}>
              {enregEnCours ? 'Enregistrement…' : `Confirmer et enregistrer (${recap.lignesValidees} ligne(s))`}
            </button>
            {!recap.pretAEnregistrer && <small className="note">Disponible quand toutes les lignes sont validées, sans valeur à corriger ni erreur.</small>}
          </div>
        </div>
      )}
      {succes && <div className="message ok" role="status">{succes}</div>}
    </section>
  );
}
